import { Capacitor, registerPlugin } from '@capacitor/core'

/**
 * Cross-platform "get my current position" used by in-call AND in-thread location
 * sharing.
 *
 * iOS WKWebView does NOT implement the HTML5 `navigator.geolocation` bridge, so
 * on a native build `navigator.geolocation.getCurrentPosition` silently never
 * resolves — which is why sharing location did nothing on iOS. When the native
 * `@capacitor/geolocation` plugin is present we use it (it talks to Core Location
 * and shows the iOS permission prompt, backed by the
 * `NSLocationWhenInUseUsageDescription` key in Info.plist); otherwise we fall
 * back to the web API (works on web + Android WebView).
 *
 * The plugin is referenced via `registerPlugin` only, so this file has NO build
 * dependency on `@capacitor/geolocation`: if the pod isn't installed,
 * `isPluginAvailable('Geolocation')` is false and we use the web fallback.
 *
 * Reliability: a single high-accuracy `getCurrentPosition` regularly REJECTS on
 * iOS indoors (no fresh GPS fix within the timeout) — that surfaced as the
 * recurring "Couldn't share location — please try again". We now (1) check/ask
 * permission explicitly and (2) attempt a fast high-accuracy fix, then fall back
 * to a coarser/cached fix before giving up, and throw a typed error so callers
 * can tell "denied" apart from "couldn't get a fix".
 */

type NativePermissionStatus = {
  location?: string
  coarseLocation?: string
}

type NativePosition = {
  coords: { latitude: number; longitude: number; accuracy?: number }
}

type NativeGeolocationPlugin = {
  getCurrentPosition(options?: {
    enableHighAccuracy?: boolean
    timeout?: number
    maximumAge?: number
  }): Promise<NativePosition>
  checkPermissions?(): Promise<NativePermissionStatus>
  requestPermissions?(permissions?: {
    permissions?: string[]
  }): Promise<NativePermissionStatus>
}

const NativeGeolocation = registerPlugin<NativeGeolocationPlugin>('Geolocation')

export type SharedPosition = { latitude: number; longitude: number; accuracy?: number }

export type GeolocationFailureReason = 'denied' | 'unavailable' | 'timeout'

/** Typed failure so the UI can show the right message / a "open settings" action. */
export class GeolocationShareError extends Error {
  reason: GeolocationFailureReason
  constructor(reason: GeolocationFailureReason, message?: string) {
    super(message ?? reason)
    this.name = 'GeolocationShareError'
    this.reason = reason
  }
}

function shouldUseNativeGeolocation(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Geolocation')
}

function isGranted(state: string | undefined): boolean {
  return state === 'granted' || state === 'prompt-with-rationale' || state === 'prompt'
}

function toShared(pos: NativePosition): SharedPosition {
  return {
    latitude: pos.coords.latitude,
    longitude: pos.coords.longitude,
    accuracy: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : undefined,
  }
}

/** Ensure native location permission, prompting once. Throws on a hard denial. */
async function ensureNativePermission(): Promise<void> {
  try {
    const current = await NativeGeolocation.checkPermissions?.()
    if (current && (current.location === 'denied' || current.coarseLocation === 'denied')) {
      // Already denied — a fresh request won't re-prompt; surface a clear error.
      throw new GeolocationShareError('denied')
    }
    const after = await NativeGeolocation.requestPermissions?.({
      permissions: ['location', 'coarseLocation'],
    })
    if (after && !isGranted(after.location) && !isGranted(after.coarseLocation)) {
      throw new GeolocationShareError('denied')
    }
  } catch (err) {
    if (err instanceof GeolocationShareError) throw err
    // checkPermissions/requestPermissions can be unimplemented on some shells —
    // fall through and let getCurrentPosition do the prompting.
  }
}

async function nativePosition(): Promise<SharedPosition> {
  await ensureNativePermission()
  // 1) Fast, fresh high-accuracy fix.
  try {
    return toShared(
      await NativeGeolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 12_000,
        maximumAge: 0,
      }),
    )
  } catch {
    /* fall through to a coarser/cached attempt */
  }
  // 2) Coarser fix; accept a recent cached position rather than failing the share.
  try {
    return toShared(
      await NativeGeolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 15_000,
        maximumAge: 60_000,
      }),
    )
  } catch {
    throw new GeolocationShareError('timeout', 'Could not get a location fix')
  }
}

function webPosition(): Promise<SharedPosition> {
  if (!navigator.geolocation) {
    return Promise.reject(new GeolocationShareError('unavailable', 'Geolocation unavailable'))
  }
  const getOnce = (opts: PositionOptions) =>
    new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, opts)
    })
  return getOnce({ enableHighAccuracy: true, timeout: 12_000, maximumAge: 0 })
    .catch(() => getOnce({ enableHighAccuracy: false, timeout: 15_000, maximumAge: 60_000 }))
    .then(
      (pos) => ({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : undefined,
      }),
      (err: GeolocationPositionError | undefined) => {
        if (err && err.code === err.PERMISSION_DENIED) {
          throw new GeolocationShareError('denied')
        }
        throw new GeolocationShareError('timeout', 'Could not get a location fix')
      },
    )
}

/** Resolve a high-accuracy position, preferring the native plugin on iOS/Android. */
export async function getCurrentPositionShared(): Promise<SharedPosition> {
  if (shouldUseNativeGeolocation()) return nativePosition()
  return webPosition()
}
